import React, { useEffect, useRef, useState } from "react";
import { FileInput, FileField, SimpleForm, useRefresh, useNotify, fetchStart, fetchEnd } from "react-admin";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch } from 'react-redux';
import { useForm, useFormState } from 'react-final-form';
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import LinearProgress from "@material-ui/core/LinearProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import GetAppIcon from "@material-ui/icons/GetApp";
import axios from "axios";
import {apiUrl} from "../../dataProvider";
import { rawFileExtraction } from "../../dataProvider";
import CustomStoreSelect from "../CustomStoreSelect";


const useStyles = makeStyles(() => ({
    fileUpload: {
        width: '100%',
    },
    link: {
        marginBottom: '10px',
        cursor: 'pointer',
    },
    jobsTable: {
        marginTop: 24,
        width: "100%",
    },
    jobsProgressCell: {
        minWidth: 160,
    },
    progressWrap: {
        background: "linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "8px 10px",
    },
    progressMeta: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 6,
        fontSize: 11,
        color: "#475569",
    },
    progressValue: {
        fontWeight: 700,
        color: "#1d4ed8",
    },
    progressBar: {
        height: 8,
        borderRadius: 999,
        backgroundColor: "#dbeafe",
        "& .MuiLinearProgress-barColorPrimary": {
            borderRadius: 999,
            background: "linear-gradient(90deg, #22c55e 0%, #14b8a6 50%, #3b82f6 100%)",
        },
    },
    warnLabel: {
        fontSize: "12px",
        color: "red",
        display: "block"
    },
}));

const toTitleCase = (value) => {
    if (typeof value !== "string") return value;
    const parts = value.split(/[_-]+/).filter(Boolean);
    if (parts.length === 0) return value;
    return parts
        .map((p) => {
            const s = p.toLowerCase();
            return s.charAt(0).toUpperCase() + s.slice(1);
        })
        .join(" ");
};

const FileUpload = props => {
    const notify = useNotify();
    const form = useForm();
    const { values } = useFormState({ subscription: { values: true } });
    const selectedFiles = values?.files;
    const hasSelectedFile = Array.isArray(selectedFiles)
        ? selectedFiles.length > 0
        : !!selectedFiles;
    const isDisabled = !hasSelectedFile;

    const handleUpload = async () => {
        const file = { file: selectedFiles };
        if (!file.file || (Array.isArray(file.file) && file.file.length === 0)) {
            notify("Please select a file to upload");
            return;
        }

        const formData = await rawFileExtraction(file);
        props.uploadFile(formData).then(() => {
            form.change("files", []);
        });
    }

    return (
        <>
            <Link
                onClick={props.downloadSample}
                className={props.classes?.link}
                style={{ display: "block", marginBottom: 16 }}
            >
                Download Sample
            </Link>
            <Button
                variant="outlined"
                disabled={isDisabled}
                onClick={handleUpload}
                fullWidth
                
            >
                Start Price Scraping
            </Button>
        </>
    );
};

export default () => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const notify = useNotify();
    const refresh = useRefresh();

    const [storeId, setStoreId] = useState(null);

    const [jobsLoading, setJobsLoading] = useState(false);
    const [jobs, setJobs] = useState([]);
    const jobsPollingTimeoutRef = useRef(null);

    const fetchJobs = async () => {
        if (jobsPollingTimeoutRef.current) {
            clearTimeout(jobsPollingTimeoutRef.current);
            jobsPollingTimeoutRef.current = null;
        }

        setJobsLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/price-scraper/jobs`);
            const payload = response?.data;

            // expected: { data: [...] } or [...]
            const list = Array.isArray(payload?.data)
                ? payload.data
                : Array.isArray(payload)
                    ? payload
                    : Array.isArray(payload?.jobs)
                        ? payload.jobs
                        : [];

            const normalizedList = Array.isArray(list) ? list : [];
            setJobs(normalizedList);

            const activeStatuses = ["pending", "processing"];
            const hasActiveJobs = normalizedList.some((job) =>
                activeStatuses.includes(String(job?.status || "").toLowerCase())
            );

            if (hasActiveJobs) {
                jobsPollingTimeoutRef.current = setTimeout(() => {
                    fetchJobs();
                }, 5000);
            }
        } catch (e) {
            notify("Unable to fetch price scraper jobs", "warning");
            setJobs([]);
        } finally {
            setJobsLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs();

        return () => {
            if (jobsPollingTimeoutRef.current) {
                clearTimeout(jobsPollingTimeoutRef.current);
                jobsPollingTimeoutRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const uploadFile = async file => {
        setLoading(true);
        dispatch(fetchStart());

        let axiosConfig = {
            headers: {
                "Content-Type": "multipart/form-data",
                "Access-Control-Allow-Origin": "*"
            }
        };
        if (storeId != null && file && typeof file.append === "function") {
            file.append("store_id", storeId);
        }

        await axios
            .post(`${apiUrl}/price-scraper`, file, axiosConfig)
            .then(response => {
                notify(response?.data?.message);
                refresh();
                fetchJobs();
                // job list may populate slightly after the upload call returns
                setTimeout(() => fetchJobs(), 6000);
                return {
                    data: {
                        ...response.data
                    }
                };
            })
            .catch(({response: {data: {errors}}}) => {
                const message = errors[Object.keys(errors)[0]][0];
                notify(message, 'warning')
            })
            .finally(() => {
                setLoading(false);
                dispatch(fetchEnd());
            });
    };

    const downloadSample = () => {
        let axiosConfig = {
            headers:
                {
                    'Content-Disposition': "attachment; filename=price_scraper_sample.csv",
                    'Content-Type': 'text/csv'
                },
            responseType: 'arraybuffer',
        }

        axios.get(`${apiUrl}/price-scraper/download-sample`, axiosConfig)
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'price_scraper_sample.csv');
                document.body.appendChild(link);
                link.click();
            });
    };

    const downloadJobFile = (jobId, type, fallbackFileName) => {
        if (!jobId) return;

        const suffix = type === "input" ? "download-input" : "download-output";
        const url = `${apiUrl}/price-scraper/jobs/${encodeURIComponent(jobId)}/${suffix}`;
        const defaultName = type === "input" ? "input_price_scraper.csv" : "output_price_scraper.csv";
        const fileName = fallbackFileName || defaultName;

        const axiosConfig = {
            responseType: "arraybuffer",
            headers: {
                'Content-Disposition': `attachment; filename=${fileName}`,
                'Content-Type': 'text/csv'
            }
        };

        axios
            .get(url, axiosConfig)
            .then((response) => {
                const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
                const link = document.createElement("a");
                link.href = blobUrl;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();
            })
            .catch(() => {
                notify(`Unable to download ${type} file`, "warning");
            });
    };

    const handleFileReject = () => notify("Please upload the correct file format");
    return (
        <Card>
            <CardContent>
                <Grid container xs={12} justify="space-between" alignItems="start">
                    <Grid item xs={5}>
                        <Grid container spacing={2} style={{ marginBottom: 16 }}>
                            <Grid item xs={12}>
                                    <label
                                        style={{
                                            display: "block",
                                            marginBottom: "8px",
                                            fontSize: "0.875rem",
                                            color: "rgba(0, 0, 0, 0.54)"
                                        }}
                                    >
                                        Store
                                    </label>
                                    <CustomStoreSelect storeId={storeId} onStoreIdChange={setStoreId} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={5}>
                        <SimpleForm toolbar={null}>

                            <Grid item container justify="center" className={classes.fileUpload}>
                                <Grid item xs={12}>
                                    <FileInput
                                        source="files"
                                        label="Upload Magic-Number/Size CSV"
                                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                        multiple={false}
                                        options={{onDropRejected: handleFileReject}}
                                        placeholder={
                                            <p>
                                                Drop a file to upload, or click to select it.
                                                <span className={classes.warnLabel}>
                                        *File format must be .csv
                                    </span>
                                            </p>
                                        }
                                    >
                                        <FileField source="src" title="title" />
                                    </FileInput>
                                </Grid>
                            </Grid>
                            <Grid item container justify="center" className={classes.fileUpload}>
                            <FileUpload
                                uploadFile={uploadFile}
                                downloadSample={downloadSample}
                                classes={classes}
                            />
                            </Grid>
                        </SimpleForm>
                    </Grid>
                </Grid>
                <Grid container xs={12}>
                    <Table className={classes.jobsTable} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Original File Name</TableCell>
                                <TableCell>Store</TableCell>
                                <TableCell className={classes.jobsProgressCell}>Progress</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Input File</TableCell>
                                <TableCell align="center">Output File</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {jobsLoading && (
                                <TableRow>
                                    <TableCell colSpan={5}>Loading jobs...</TableCell>
                                </TableRow>
                            )}
                            {!jobsLoading && jobs.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5}>No jobs found</TableCell>
                                </TableRow>
                            )}
                            {!jobsLoading &&
                                jobs.map((job, idx) => {
                                    const rawStatus = job?.status;
                                    const status = rawStatus ? toTitleCase(rawStatus) : "N/A";

                                    const jobId = job?.job_id ?? job?.id ?? null;
                                    const originalFileName =
                                        job?.original_filename ??
                                        "N/A";

                                    const rawProgress =
                                        job?.progress_percentage ??
                                        job?.progress_percent ??
                                        job?.progress ??
                                        job?.progressPercent ??
                                        null;
                                    let progressValue = null;
                                    if (rawProgress !== null && rawProgress !== undefined) {
                                        const n = Number(rawProgress);
                                        progressValue = Number.isFinite(n)
                                            ? n <= 1 && n >= 0
                                                ? n * 100
                                                : n
                                            : null;
                                    }
                                    const progressText =
                                        progressValue === null ? "N/A" : `${Math.round(progressValue)}%`;

                                    const inputFileUrl =
                                        jobId && job?.has_input_file
                                            ? `${apiUrl}/price-scraper/jobs/${encodeURIComponent(jobId)}/download-input`
                                            : null;

                                    const outputFileUrl =
                                        jobId && job?.has_output_file
                                            ? `${apiUrl}/price-scraper/jobs/${encodeURIComponent(jobId)}/download-output`
                                            : null;

                                    const hasInputFile = !!job?.has_input_file;
                                    const hasOutputFile = !!job?.has_output_file;

                                    return (
                                        <TableRow key={jobId ?? idx}>
                                            <TableCell>{originalFileName}</TableCell>
                                            <TableCell>{job?.store ?? "-"}</TableCell>
                                            <TableCell>
                                                {progressValue === null ? (
                                                    "N/A"
                                                ) : (
                                                    <div className={classes.progressWrap}>
                                                        <div className={classes.progressMeta}>
                                                            <span></span>
                                                            <span className={classes.progressValue}>{progressText}</span>
                                                        </div>
                                                        <LinearProgress
                                                            className={classes.progressBar}
                                                            variant="determinate"
                                                            value={Math.min(100, Math.max(0, progressValue))}
                                                        />
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>{status}</TableCell>
                                            <TableCell align="center">
                                                {(() => {
                                                    const disabled = !hasInputFile || !inputFileUrl;
                                                    const tooltipTitle = disabled
                                                        ? "Input File is not generated yet"
                                                        : "Download input file";

                                                    if (disabled) {
                                                        return (
                                                            <Tooltip title={tooltipTitle} arrow>
                                                                <span>
                                                                    <IconButton disabled style={{ color: "rgba(0,0,0,0.26)" }}>
                                                                        <GetAppIcon />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        );
                                                    }

                                                    return (
                                                        <Tooltip title={tooltipTitle} arrow>
                                                            <IconButton
                                                                style={{ color: "green" }}
                                                                onClick={() =>
                                                                    downloadJobFile(
                                                                        jobId,
                                                                        "input",
                                                                        job?.original_filename || "price_scraper_input.csv"
                                                                    )
                                                                }
                                                            >
                                                                <GetAppIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell align="center">
                                                {(() => {
                                                    const disabled = !hasOutputFile || !outputFileUrl;
                                                    const tooltipTitle = disabled
                                                        ? "Output File is not generated yet"
                                                        : "Download output file";

                                                    if (disabled) {
                                                        return (
                                                            <Tooltip title={tooltipTitle} arrow>
                                                                <span>
                                                                    <IconButton disabled style={{ color: "rgba(0,0,0,0.26)" }}>
                                                                        <GetAppIcon />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        );
                                                    }

                                                    return (
                                                        <Tooltip title={tooltipTitle} arrow>
                                                            <IconButton
                                                                style={{ color: "green" }}
                                                                onClick={() =>
                                                                    downloadJobFile(
                                                                        jobId,
                                                                        "output",
                                                                        "price_scraper_output.csv"
                                                                    )
                                                                }
                                                            >
                                                                <GetAppIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    );
                                                })()}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                    <div style={{ marginTop: 8, color: "rgba(0, 0, 0, 0.6)", fontSize: 12 }}>
                        Note: Only last 10 jobs are displaying.
                    </div>
                </Grid>
            </CardContent>
        </Card>
    );
};